import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { FuseConfigService } from '@fuse/services/config.service';
import { fuseAnimations } from '@fuse/animations';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from 'app/services/auth.service';

@Component({
    selector: 'forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ForgotPasswordComponent implements OnInit, OnDestroy
{
    forgotPasswordForm: FormGroup;
    sending = false;
    sent = false;
    lastErrorMessage: string;

    private _unsubscribeAll: Subject<void>;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FormBuilder} _formBuilder
     * @param {AuthService} authService
     * @param {TranslateService} translateService
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _formBuilder: FormBuilder,
        private authService: AuthService,
        private translateService: TranslateService
    )
    {
        // Configure the layout
        this._fuseConfigService.config = {
            layout: {
                navbar   : {
                    hidden: true
                },
                toolbar  : {
                    hidden: true
                },
                footer   : {
                    hidden: true
                },
                sidepanel: {
                    hidden: true
                }
            }
        };

        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.forgotPasswordForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]]
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Richiede la mail con il link di reimpostazione.
     */
    onSendClick(): void
    {
        if(this.forgotPasswordForm.invalid || this.sending) return;

        const email = this.forgotPasswordForm.value.email;

        this.lastErrorMessage = null;
        this.sending = true;
        this.forgotPasswordForm.disable();

        this.authService.resetPasswordByEmail(email)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(response => {
            this.sending = false;

            // ForgotPasswordController risponde 200 anche quando l'indirizzo non e'
            // registrato: l'esito sta in `success`, non nello stato HTTP
            if(response && response.success === false){
                this.forgotPasswordForm.enable();
                this.lastErrorMessage = response.message || this.translateService.instant('FORGOT_PASSWORD.ERROR.GENERIC');
                return;
            }

            this.sent = true;
        }, response => {
            // handleError mostra gia' la snackbar, qui si ripristina il form
            this.sending = false;
            this.forgotPasswordForm.enable();
            this.lastErrorMessage = this.translateService.instant('FORGOT_PASSWORD.ERROR.GENERIC');
            if(response instanceof HttpErrorResponse && response.error && response.error.message){
                this.lastErrorMessage = response.error.message;
            }
        });
    }
}
